import {createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {replyTargetForPath, type ReplyTarget} from "./replyPrompt";

interface ReplyContext {
	target: ReplyTarget | null;
	available: boolean;
	register: (target: ReplyTarget) => void;
	anchor: HTMLElement | null;
	open: boolean;
	show: (target: ReplyTarget, anchor: HTMLElement, opener: HTMLElement) => void;
	close: () => void;
}
const Context = createContext<ReplyContext | null>(null);

/** App-owned, so closing the panel or returning home retains the composer's files. */
export function DownloadReplyProvider({children}: {children: ReactNode}) {
	const {pathname} = useLocation();
	const returnFocus = useRef<HTMLElement | null>(null);
	const [registered, register] = useState<ReplyTarget | null>(null);
	const [state, setState] = useState<{target: ReplyTarget; anchor: HTMLElement; opener: HTMLElement; path: string; open: boolean} | null>(null);
	const show = useCallback((target: ReplyTarget, anchor: HTMLElement, opener: HTMLElement) => {
		setState({target, anchor, opener, path: pathname, open: true});
	}, [pathname]);
	const close = useCallback(() => {
		returnFocus.current = state?.opener ?? null;
		setState(current => current ? {...current, open: false} : null);
	}, [state]);
	useIsomorphicLayoutEffect(() => {
		if (!state?.open && returnFocus.current) {
			returnFocus.current.focus({preventScroll: true});
			returnFocus.current = null;
		}
	}, [state?.open]);
	useEffect(() => { setState(current => current ? {...current, open: false} : null); }, [pathname]);
	const available = replyTargetForPath(pathname, registered) !== null;
	const value = useMemo(() => ({target: state?.target ?? null, anchor: state?.anchor ?? null,
		available, register, open: state?.open === true && state.path === pathname, show, close}), [state, pathname, available, show, close]);
	return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useDownloadReply(): ReplyContext {
	const context = useContext(Context);
	if (!context) throw new Error("DownloadReplyProvider is required");
	return context;
}
